import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { ApiError } from '@/api/client'

/**
 * Keeping things on the device for when there is no signal (N-06).
 *
 * On a phone this is native Preferences, which is written to disk straight
 * away and survives the WebView being cleared; in a browser it is
 * localStorage. Everything here is a convenience copy: the server stays the
 * record, and a missing or unreadable copy is treated as nothing kept.
 */
const PREFIX = 'refuelled.offline.'

/** Every key written here, so signing out can take them all away. */
export const OFFLINE_KEYS = ['shopping.cache', 'shopping.queue'] as const
export type OfflineKey = (typeof OFFLINE_KEYS)[number]

const isNative = () => {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

export async function readOffline<T>(key: OfflineKey): Promise<T | null> {
  try {
    const raw = isNative()
      ? (await Preferences.get({ key: PREFIX + key })).value
      : localStorage.getItem(PREFIX + key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

export async function writeOffline(key: OfflineKey, value: unknown): Promise<void> {
  const raw = JSON.stringify(value)
  try {
    if (isNative()) await Preferences.set({ key: PREFIX + key, value: raw })
    else localStorage.setItem(PREFIX + key, raw)
  } catch {
    // A full or unavailable store loses the offline copy, not the change.
  }
}

/** On signing out: nothing of one person's is left for the next. */
export async function clearOffline(): Promise<void> {
  for (const key of OFFLINE_KEYS) {
    try {
      if (isNative()) await Preferences.remove({ key: PREFIX + key })
      localStorage.removeItem(PREFIX + key)
    } catch {
      // Nothing to clear.
    }
  }
}

/**
 * The request never reached the server, as opposed to the server saying no.
 * `fetch` rejects with a TypeError when there is no connection; anything the
 * server answered is an ApiError.
 */
export function isNetworkError(err: unknown): boolean {
  return !(err instanceof ApiError) && err instanceof TypeError
}
